import { debounce } from 'lodash/';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobileOrTablet, WithBreakpointRange } from '@mews-commander/ui';
import { DebounceTimeoutValues } from '@optimus-web/common';
import { useClickAway } from '@optimus-web/helpers';
import { SearchBar } from './searchBar/searchBar';
import { useAssistantSearchQuery } from './query/useAssistantSearchQuery';
import { AssistantContainer } from './styles';
import { AdditionalSearchType, AssistantSearchType, SearchSubSection, SearchType } from './assistantSearchTypes';
import { Drawer } from './drawer/Drawer';
import { useSearchResultList } from './drawer/hooks/useSearchResultList';
import { useRecentAndFavoriteList } from './drawer/hooks/useRecentAndFavoriteList';
import { useAssistantResultDetails } from './hooks/useAssistantResultDetails';
import { usePropertySelectorComponent } from './hooks/usePropertySelectorComponent';
import { AssistantFilterChips } from './assistantSearchTypeFilters';
import { useAssistantListItemSelectionHandler } from './drawer/hooks/useAssistantListItemSelectionHandler';
import { useResultFilterChipComponent } from './hooks/useResultFilterChipComponent';
import { AssistantSearchListItemProps } from './drawer/hooks/useAssistantSearchResultListMap';
import { useHandleSearchShortcuts } from './hooks/useHandleSearchShortcuts';

interface AssistantProps extends WithBreakpointRange {
    mobileSearchOpened?: boolean;
    handleBackClick: () => void;
}

export interface SearchStateProps {
    searchTerm: string;
    mqlSearchTerm?: string;
    recentSearchTerm: string;
    focused: boolean;
    autoComplete: boolean;
}

export interface FilterStateProps {
    firstLevel: AssistantSearchType | AssistantFilterChips;
    secondLevel: AssistantFilterChips | undefined;
    selectedSearchType: AssistantSearchType;
}

export interface ShowMoreOrLessStateProps {
    itemsToShow: Record<AdditionalSearchType | SearchType | SearchSubSection, number>;
    initialItemsToShow: Record<AdditionalSearchType | SearchType | SearchSubSection, number>;
}

export const Assistant = ({ breakpointRange, mobileSearchOpened, handleBackClick }: AssistantProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const summarySidePanelRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const mobileOrTablet = isMobileOrTablet(breakpointRange);
    const [searchState, setSearchState] = useState<SearchStateProps>({
        searchTerm: '',
        recentSearchTerm: '',
        mqlSearchTerm: '',
        focused: false,
        autoComplete: false,
    });
    const { searchTerm, focused, recentSearchTerm, autoComplete, mqlSearchTerm } = searchState;
    const [selectedFavoritePrompt, setSelectedFavoritePrompt] = useState<AssistantSearchListItemProps>();
    const showRecent = useMemo(() => searchTerm?.length < 1, [searchTerm?.length]);

    const focusSearchInput = useCallback(() => {
        searchRef.current?.focus();
    }, []);

    /** Assistant list item selection state */
    const { setFocusableElements, handleListItemsUnFocused, listItemSelectionState, setListItemSelectionState } =
        useAssistantListItemSelectionHandler(focused, focusSearchInput, showRecent);
    const { selectedIndex, isTabNavigation } = listItemSelectionState;

    /** Assistant list item show more items state */
    const initialNumberOfRows = 5;
    const initialItemsToShow: Record<AdditionalSearchType | SearchType | SearchSubSection, number> = Object.assign(
        {},
        ...Object.keys(SearchType).map((key) => ({ [key]: initialNumberOfRows }))
    );

    const [showMoreOrLessState, setShowMoreOrLessState] = useState<ShowMoreOrLessStateProps>({
        itemsToShow: initialItemsToShow,
        initialItemsToShow,
    });

    const [filterState, setFilterState] = useState<FilterStateProps>({
        firstLevel: AdditionalSearchType.All,
        selectedSearchType: AdditionalSearchType.All,
        secondLevel: undefined,
    });
    const { selectedSearchType, firstLevel, secondLevel } = filterState;
    const { itemsToShow } = showMoreOrLessState;
    const { registerClickAway, deregisterClickAway } = useClickAway();

    /** Property selector */
    const { enterpriseIds } = usePropertySelectorComponent(setSearchState);

    const handleUnFocused = useCallback(() => {
        setSearchState((prev) => ({
            ...prev,
            searchTerm: '',
            recentSearchTerm: '',
            mqlSearchTerm: '',
            focused: false,
            autoComplete: false,
        }));
        setFilterState((prev) => ({
            ...prev,
            selectedSearchType: AdditionalSearchType.All,
            secondLevel: undefined,
            firstLevel: AdditionalSearchType.All,
        }));
        setShowMoreOrLessState((prev) => ({
            ...prev,
            itemsToShow: prev.initialItemsToShow,
        }));
        deregisterClickAway();
        searchRef.current?.blur();
    }, [deregisterClickAway]);

    const handleLocalBackClick = () => {
        handleUnFocused();
        handleBackClick();
    };

    useHandleSearchShortcuts(setSearchState, handleUnFocused);

    /** Recent and Favorites */
    const {
        recentAndFavoriteListComponent,
        recentAndFavoriteFetching,
        favoriteListItems,
        recentAndFavoriteMergedList,
        addRecent,
    } = useRecentAndFavoriteList({
        recentSearchTerm,
        selectedIndex,
        setSearchState,
        setFilterState,
        isTabNavigation,
    });

    /** Query search */
    const {
        data: assistantResult,
        isFetching,
        isFetched,
    } = useAssistantSearchQuery(mqlSearchTerm || searchTerm, enterpriseIds);
    const onSearch = useCallback(
        (term: string, mqlTerm?: string) => {
            if (term.trimEnd()) {
                addRecent(term);
            }

            setSearchState((prev) => ({
                ...prev,
                searchTerm: term,
                mqlSearchTerm: mqlTerm || undefined,
                recentSearchTerm: '',
                autoComplete: false,
            }));
            setFilterState((prev) => ({
                ...prev,
                selectedSearchType: AdditionalSearchType.All,
                secondLevel: undefined,
                firstLevel: AdditionalSearchType.All,
            }));
            setShowMoreOrLessState((prev) => ({
                ...prev,
                itemsToShow: prev.initialItemsToShow,
            }));
        },
        [addRecent]
    );
    const handleSearch = useMemo(() => debounce(onSearch, DebounceTimeoutValues.textInput), [onSearch]);

    /** Drawer */
    const { hasResult, assistantSearchResultListMap, availableSearchTypeKeyList } = useAssistantResultDetails({
        searchTerm,
        assistantResult,
        filterState,
    });
    const { searchResultList } = useSearchResultList({
        hasResult,
        selectedSearchType,
        assistantSearchResultListMap,
        setFilterState,
        itemsToShow,
    });

    const resultFilterChipComponent = useResultFilterChipComponent({
        searchTypeKeyList: availableSearchTypeKeyList,
        setSearchState,
        selectedSearchType,
        results: assistantResult?.searchResult,
        setFilterState,
    });

    useEffect(() => {
        if (focused) {
            searchRef.current?.focus();
            registerClickAway({
                handler: handleUnFocused,
                roots: [() => ref.current, () => summarySidePanelRef.current],
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focused]);

    useEffect(() => {
        if (selectedSearchType && searchTerm?.trimEnd().length > 0 && searchRef.current) {
            handleListItemsUnFocused();
            searchRef.current.focus();
        }
    }, [handleListItemsUnFocused, searchTerm, selectedSearchType]);

    /** set focusable elements */
    useEffect(() => {
        const assistantRefElement = ref.current;
        if (
            assistantRefElement &&
            !autoComplete &&
            focused &&
            ((showRecent && !recentAndFavoriteFetching && recentAndFavoriteMergedList.length > 0) ||
                (!showRecent &&
                    hasResult &&
                    selectedSearchType &&
                    itemsToShow &&
                    isFetched &&
                    assistantResult &&
                    (firstLevel || secondLevel || selectedFavoritePrompt)))
        ) {
            const focusable = assistantRefElement.querySelectorAll(
                'button, [href], input, select, textarea, [visibility], [tabindex]'
            );
            const elements = Array.from(focusable).map((el) => el as HTMLElement);
            setFocusableElements(elements);
            handleListItemsUnFocused();
        } else {
            handleListItemsUnFocused();
            setFocusableElements([]);
        }
    }, [
        focused,
        autoComplete,
        showRecent,
        hasResult,
        selectedSearchType,
        itemsToShow,
        isFetched,
        assistantResult,
        firstLevel,
        secondLevel,
        handleListItemsUnFocused,
        setFocusableElements,
        recentAndFavoriteFetching,
        selectedFavoritePrompt,
        recentAndFavoriteMergedList,
    ]);

    const showAutocomplete: boolean = focused && !isFetching && !hasResult;

    return (
        <Fragment>
            <AssistantContainer ref={ref} isMobileOrTablet={mobileOrTablet} mobileSearchOpened={mobileSearchOpened}>
                <SearchBar
                    ref={searchRef}
                    loading={isFetching}
                    mobileOrTablet={mobileOrTablet}
                    focused={focused}
                    enterpriseIds={enterpriseIds}
                    handleBackClick={handleLocalBackClick}
                    handleSearch={handleSearch}
                    setSearchState={setSearchState}
                    showRecent={showRecent}
                    showAutocomplete={showAutocomplete}
                    searchTerm={searchTerm}
                    favoriteListItems={favoriteListItems}
                    recentSearchTerm={recentSearchTerm}
                    setListItemSelectionState={setListItemSelectionState}
                    selectedFavoritePrompt={selectedFavoritePrompt}
                    setSelectedFavoritePrompt={setSelectedFavoritePrompt}
                />
                <Drawer
                    ref={summarySidePanelRef}
                    isMobileOrTablet={mobileOrTablet}
                    isFetching={isFetching}
                    assistantResult={assistantResult}
                    hasResult={hasResult}
                    isFetched={isFetched}
                    recentAndFavoriteListComponent={recentAndFavoriteListComponent}
                    setSearchState={setSearchState}
                    resultFilterChipComponent={resultFilterChipComponent}
                    setShowMoreOrLessState={setShowMoreOrLessState}
                    showMoreOrLessState={showMoreOrLessState}
                    assistantSearchResultListMap={assistantSearchResultListMap}
                    searchResultList={searchResultList}
                    searchState={searchState}
                    selectedSearchType={selectedSearchType}
                    enterpriseIds={enterpriseIds}
                    recentAndFavoriteFetching={recentAndFavoriteFetching}
                    handleUnFocused={handleUnFocused}
                    setListItemSelectionState={setListItemSelectionState}
                    listItemSelectionState={listItemSelectionState}
                    isTabNavigation={isTabNavigation}
                />
            </AssistantContainer>
        </Fragment>
    );
};
